/**
 * A chosen value drawn as a removable chip — "Pune ×", "Nashik ×" — and the
 * dashed slot that adds another. The concept's `.sb-chip` and `.sb-select--add`.
 *
 * ## WHY VIOLET AND NOT THE CONCEPT'S GREEN
 *
 * The concept drew every chosen value in `good`'s green. Green in this product
 * means a verdict — delivered, matched, healthy — and a city somebody typed
 * into a rule is not good news, it is a value. Beside a coverage reading in
 * the same green, a reader could not tell which green was the verdict. So a
 * value is violet, the colour of "part of what you are building in Wend":
 * `brand-ink-strong` on `brand-a/16`, 12.29:1.
 *
 * ## THE REMOVE BUTTON NAMES WHAT IT REMOVES
 *
 * `removeLabel` is required whenever `onRemove` is — the type makes the pair
 * inseparable. A row of five "Remove" buttons is five identical stops to a
 * screen reader; "Remove Pune" is one decision. The button is 24px, the
 * smallest target WCAG 2.2 accepts, and turns `bad` on hover because pressing
 * it discards something.
 *
 * ## THE HOLE IT LEAVES
 *
 * `AddChip` is only a skin: it wraps whatever control the caller supplies (a
 * `Select`, an input) and does not know what adding means. Focus after a
 * removal is also the caller's — the chip that had it is gone, and only the
 * screen knows whether focus belongs on the next chip or the add slot.
 */
import { X } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';

interface ValueChipBase extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  label: ReactNode;
}

export type ValueChipProps = ValueChipBase &
  ({ onRemove?: undefined; removeLabel?: undefined } | { onRemove: () => void; removeLabel: string });

export function ValueChip({ label, onRemove, removeLabel, className, ...rest }: ValueChipProps) {
  const removable = onRemove !== undefined;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 rounded-sm border border-brand-b/30 bg-brand-a/16 py-0.5 text-[0.84rem] leading-snug text-brand-ink-strong',
        removable ? 'pl-2 pr-0.5' : 'px-2',
        className,
      )}
      {...rest}
    >
      <span>{label}</span>
      {removable && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className={cn(
            'grid size-6 cursor-pointer place-items-center rounded-xs text-brand-ink transition-colors hover:bg-bad/16 hover:text-bad',
            focusRing,
          )}
        >
          <X aria-hidden="true" className="size-3" />
        </button>
      )}
    </span>
  );
}

export interface AddChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** The control that adds a value — a Select, an input. */
  children: ReactNode;
}

/** A dashed slot for the control that adds the next value. */
export function AddChip({ className, ...rest }: AddChipProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm border border-dashed border-line-strong text-[0.78rem] text-muted',
        className,
      )}
      {...rest}
    />
  );
}
